import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { GuideStatus } from "@mapmycustomers/shared";
import { Role } from "@mapmycustomers/shared/enum";
import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import ViewMode from "@app/scene/onboarding/enums/ViewMode";
import { getGuides, getGuidesStatus, isLoading } from "@app/scene/onboarding/store";
import { initializeMemberView, refreshGuidesStatus } from "@app/scene/onboarding/store/actions";
import getUsersStatusMap from "@app/scene/onboarding/utils/getUsersStatusMap";
import { getCurrentUserId, getRole } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useInterval from "@app/util/hook/useInterval";

import Subheader from "./components/Subheader";
import Guides from "./components/UserOnboarding/Guides";
import GuidesStats from "./components/UserOnboarding/GuidesStats";
import UserFilterBar from "./components/UserOnboarding/UserFilterBar";
import styles from "./OnboardingPageCommon.module.scss";

const messages = defineMessages({
  title: {
    id: "onboarding.user.title",
    defaultMessage: "Guides",
    description: "Title on the User Guides page",
  },
});

export interface UserOnboardingPageProps {
  currentUserId: User["id"];
  currentUserRole: User["role"];
  guides: Guide[];
  guidesStatus: GuideUserStatus[];
  loading?: boolean;
  onInitialize: () => void;
  onRefreshGuideStatus: () => void;
}

const UserOnboardingPage: FC<UserOnboardingPageProps> = ({
  currentUserId,
  currentUserRole,
  guides,
  guidesStatus,
  loading,
  onInitialize,
  onRefreshGuideStatus,
}) => {
  const intl = useIntl();

  useEffect(() => {
    onInitialize();
  }, [onInitialize]);

  useInterval(onRefreshGuideStatus, true, 15_000);

  const [quickFilter, setQuickFilter] = useState<string>("");
  const [completionFilter, setCompletionFilter] = useState<GuideStatus | undefined>();

  const handleResetFilters = useCallback(() => {
    setQuickFilter("");
    setCompletionFilter(undefined);
  }, []);

  const userStatuses = useMemo(
    () => getUsersStatusMap(guidesStatus)[currentUserId] ?? [],
    [guidesStatus, currentUserId]
  );
  const guidesStatusMap = useMemo(() => {
    return userStatuses.reduce(
      (result, item) => ({ ...result, [item.guideId]: item }),
      {} as Record<Guide["id"], GuideUserStatus>
    );
  }, [userStatuses]);

  const filteredGuides = useMemo(
    () =>
      guides.filter((guide) => {
        if (
          (quickFilter &&
            !guide.displayName.toLowerCase().includes(quickFilter.trim().toLowerCase())) ||
          (completionFilter &&
            (!(guide.id in guidesStatusMap) ||
              guidesStatusMap[guide.id].status !== completionFilter))
        ) {
          return false;
        }
        return true;
      }),
    [guides, quickFilter, completionFilter, guidesStatusMap]
  );

  return (
    <div className={styles.container}>
      <Subheader
        showModeSwitch={[Role.MANAGER, Role.OWNER].includes(currentUserRole.key)}
        title={intl.formatMessage(messages.title)}
        totalFilteredRecords={filteredGuides.length}
        totalRecords={guides.length}
        viewMode={ViewMode.MY}
      />
      <div className={styles.content}>
        <UserFilterBar
          completionFilter={completionFilter}
          onCompletionFilterChange={setCompletionFilter}
          onQuickFilterChange={setQuickFilter}
          quickFilter={quickFilter}
        />
        <div className={styles.delimiter} />
        {guides.length > 0 && <GuidesStats guides={guides} guidesStatusMap={guidesStatusMap} />}
        <Guides
          guides={filteredGuides}
          guidesStatusMap={guidesStatusMap}
          loading={loading}
          onResetFilters={handleResetFilters}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  currentUserRole: getRole(state)!,
  guides: getGuides(state),
  guidesStatus: getGuidesStatus(state),
  loading: isLoading(state),
});

const mapDispatchToProps = {
  onInitialize: initializeMemberView.request,
  onRefreshGuideStatus: refreshGuidesStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserOnboardingPage);
